/**
 * 借币信息：按交易所 + 币种查询 loan-info 当前快照与历史（ClickHouse）
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { API_CONFIG } from '@/config/api';
import {
  LOAN_EXCHANGES,
  getLoanRatesCurrent,
  getLoanRatesHistory,
  parseLoanTs,
  type LoanSnapshotRow,
} from '@/services/loanInfoApi';
import '@/components/ChartTooltip/ChartTooltip.css';
import './LoanInfo.css';

const CHART_HEIGHT = 300;
const CHART_PADDING = { top: 16, bottom: 32, left: 72 };
const CHART_RIGHT_PAD_RATE_ONLY = 16;
const CHART_RIGHT_PAD_DUAL = 72;
const SERIES_COLORS = ['#059669', '#7c3aed', '#0ea5e9', '#f59e0b', '#ef4444'];

function normalizeRow(r: Record<string, unknown>): LoanSnapshotRow {
  const br = r.borrowable;
  let borrowable: boolean | null = null;
  if (br === true || br === 1) borrowable = true;
  else if (br === false || br === 0) borrowable = false;

  return {
    ts: String(r.ts ?? ''),
    exchange: String(r.exchange ?? ''),
    currency: String(r.currency ?? ''),
    vip_level: String(r.vip_level ?? ''),
    hourly_borrow_rate: r.hourly_borrow_rate == null ? null : Number(r.hourly_borrow_rate),
    max_borrow_amount: r.max_borrow_amount == null ? null : Number(r.max_borrow_amount),
    borrowable,
    note: r.note == null || r.note === '' ? null : String(r.note),
  };
}

function formatTs(ts: string): string {
  const ms = parseLoanTs(ts);
  if (!ms) return ts;
  return new Date(ms).toLocaleString('zh-CN', { timeZone: 'UTC', hour12: false }) + ' UTC';
}

function formatRate(v: number | null): string {
  if (v == null || Number.isNaN(v)) return '—';
  if (Math.abs(v) < 1e-6) return v.toExponential(4);
  return v.toFixed(8);
}

function formatAmount(v: number | null): string {
  if (v == null || Number.isNaN(v)) return '—';
  if (Math.abs(v) >= 1e9) return v.toExponential(4);
  return v.toLocaleString('en-US', { maximumFractionDigits: 8 });
}

interface TooltipRow {
  label: string;
  value: string;
}

type LoanChartPoint = { t: number; rate: number; maxAmount: number | null };

function LoanRateChart({
  series,
}: {
  series: Array<{ key: string; points: LoanChartPoint[] }>;
}) {
  const width = 800;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{ xPx: number; yPx: number; xLabel: string; rows: TooltipRow[] } | null>(null);

  const { pathsRate, pathsAmount, xTicks, yTicks, yTicksAmount, allPoints, seriesPointsForTooltip, plotRight, hasAmountAxis } =
    useMemo(() => {
      const pointsBySeries = series.map((s) => s.points);
      const allPoints = pointsBySeries.flat();
      if (allPoints.length === 0) {
        return {
          pathsRate: [] as string[],
          pathsAmount: [] as string[],
          xTicks: [] as { x: number; label: string }[],
          yTicks: [] as { y: number; label: string }[],
          yTicksAmount: [] as { y: number; label: string }[],
          allPoints: [] as LoanChartPoint[],
          seriesPointsForTooltip: [] as {
            xPx: number;
            yPx: number;
            xLabel: string;
            key: string;
            yLabel: string;
          }[][],
          innerWidth: 0,
          plotRight: 0,
          hasAmountAxis: false,
        };
      }
      const amountPoints = allPoints.filter((p) => p.maxAmount != null && !Number.isNaN(p.maxAmount));
      const hasAmountAxis = amountPoints.length > 0;
      const rightPad = hasAmountAxis ? CHART_RIGHT_PAD_DUAL : CHART_RIGHT_PAD_RATE_ONLY;
      const innerWidth = width - CHART_PADDING.left - rightPad;
      const innerHeight = CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;
      const plotRight = CHART_PADDING.left + innerWidth;

      const rates = allPoints.map((p) => p.rate);
      const minR = Math.min(...rates);
      const maxR = Math.max(...rates);
      const range = maxR - minR || 1e-12;
      const minRate = minR - range * 0.08;
      const maxRate = maxR + range * 0.08;
      const span = maxRate - minRate || 1e-12;
      const tMin = Math.min(...allPoints.map((p) => p.t));
      const tMax = Math.max(...allPoints.map((p) => p.t));
      const tSpan = Math.max(tMax - tMin, 1);

      const pathsRate = pointsBySeries.map((points) => {
        if (points.length === 0) return '';
        const sorted = [...points].sort((a, b) => a.t - b.t);
        const pts = sorted.map((s) => {
          const x = CHART_PADDING.left + (innerWidth * (s.t - tMin)) / tSpan;
          const y = CHART_PADDING.top + innerHeight - (innerHeight * (s.rate - minRate)) / span;
          return `${x},${y}`;
        });
        return `M ${pts.join(' L ')}`;
      });

      let pathsAmount: string[] = [];
      let yTicksAmount: { y: number; label: string }[] = [];
      if (hasAmountAxis) {
        const amounts = amountPoints.map((p) => p.maxAmount!);
        const minA = Math.min(...amounts);
        const maxA = Math.max(...amounts);
        const aRange = maxA - minA || Math.max(Math.abs(maxA) * 1e-9, 1e-12);
        const minAmt = minA - aRange * 0.08;
        const maxAmt = maxA + aRange * 0.08;
        const amtSpan = maxAmt - minAmt || 1e-12;

        pathsAmount = pointsBySeries.map((points) => {
          if (points.length === 0) return '';
          const sorted = [...points].sort((a, b) => a.t - b.t);
          const segments: string[] = [];
          let current: string[] = [];
          const flush = () => {
            if (current.length === 0) return;
            segments.push(`M ${current.join(' L ')}`);
            current = [];
          };
          for (const p of sorted) {
            if (p.maxAmount == null || Number.isNaN(p.maxAmount)) {
              flush();
              continue;
            }
            const x = CHART_PADDING.left + (innerWidth * (p.t - tMin)) / tSpan;
            const y = CHART_PADDING.top + innerHeight - (innerHeight * (p.maxAmount - minAmt)) / amtSpan;
            current.push(`${x},${y}`);
          }
          flush();
          return segments.join(' ');
        });

        for (let i = 0; i <= 4; i++) {
          const v = maxAmt - (amtSpan * i) / 4;
          const y = CHART_PADDING.top + (innerHeight * i) / 4;
          yTicksAmount.push({ y, label: formatAmount(v) });
        }
      }

      const seriesPointsForTooltip = series.map((s) => {
        const sorted = [...s.points].sort((a, b) => a.t - b.t);
        return sorted.map((p) => {
          let yLabel = formatRate(p.rate) + '/h';
          if (p.maxAmount != null && !Number.isNaN(p.maxAmount)) {
            yLabel += ` · 最大可借 ${formatAmount(p.maxAmount)}`;
          }
          return {
            xPx: CHART_PADDING.left + (innerWidth * (p.t - tMin)) / tSpan,
            yPx: CHART_PADDING.top + innerHeight - (innerHeight * (p.rate - minRate)) / span,
            xLabel: new Date(p.t).toLocaleString('en-GB', { timeZone: 'UTC', hour12: false }),
            key: s.key,
            yLabel,
          };
        });
      });

      const xTicks: { x: number; label: string }[] = [];
      for (let i = 0; i <= 5; i++) {
        const t = tMin + (tSpan * i) / 5;
        const x = CHART_PADDING.left + (innerWidth * i) / 5;
        xTicks.push({
          x,
          label: new Date(t).toLocaleString('en-GB', {
            timeZone: 'UTC',
            hour12: false,
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
        });
      }
      const yTicks: { y: number; label: string }[] = [];
      for (let i = 0; i <= 4; i++) {
        const v = maxRate - (span * i) / 4;
        const y = CHART_PADDING.top + (innerHeight * i) / 4;
        yTicks.push({ y, label: formatRate(v) });
      }

      return {
        pathsRate,
        pathsAmount,
        xTicks,
        yTicks,
        yTicksAmount,
        allPoints,
        seriesPointsForTooltip,
        innerWidth,
        plotRight,
        hasAmountAxis,
      };
    }, [series]);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (seriesPointsForTooltip.length === 0 || seriesPointsForTooltip.every((a) => a.length === 0) || !wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const rows: TooltipRow[] = [];
      let xLabel = '';
      let yPx = CHART_HEIGHT / 2;
      for (const arr of seriesPointsForTooltip) {
        if (arr.length === 0) continue;
        let nearest = arr[0];
        let minD = Math.abs(nearest.xPx - mouseX);
        for (let i = 1; i < arr.length; i++) {
          const d = Math.abs(arr[i].xPx - mouseX);
          if (d < minD) {
            minD = d;
            nearest = arr[i];
          }
        }
        xLabel = nearest.xLabel;
        rows.push({ label: nearest.key, value: nearest.yLabel });
        yPx = Math.min(yPx, nearest.yPx);
      }
      const first = seriesPointsForTooltip.find((a) => a.length > 0);
      let xPx = mouseX;
      if (first && first.length > 0) {
        let nearest = first[0];
        let minD = Math.abs(nearest.xPx - mouseX);
        for (let i = 1; i < first.length; i++) {
          const d = Math.abs(first[i].xPx - mouseX);
          if (d < minD) {
            minD = d;
            nearest = first[i];
          }
        }
        xPx = nearest.xPx;
      }
      setTooltip({ xPx, yPx: yPx - 8, xLabel, rows });
    },
    [seriesPointsForTooltip]
  );

  const onMouseLeave = useCallback(() => setTooltip(null), []);

  if (allPoints.length === 0) {
    return <div className="loan-info-chart-empty">暂无利率曲线（历史记录中无 hourly_borrow_rate）</div>;
  }

  return (
    <div className="loan-info-chart-wrap chart-with-tooltip" ref={wrapRef} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
      {tooltip && (
        <div
          className="chart-tooltip"
          style={{ left: tooltip.xPx, top: tooltip.yPx, transform: 'translate(-50%, -100%)' }}
        >
          <div className="chart-tooltip-row">
            <span className="label">时间</span>
            <span className="value">{tooltip.xLabel}</span>
          </div>
          {tooltip.rows.map((r) => (
            <div key={r.label} className="chart-tooltip-row">
              <span className="label">{r.label}</span>
              <span className="value">{r.value}</span>
            </div>
          ))}
        </div>
      )}
      <svg width={width} height={CHART_HEIGHT} className="funding-history-chart">
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={CHART_PADDING.left}
              y1={t.y}
              x2={plotRight}
              y2={t.y}
              stroke="#eee"
              strokeDasharray="2,2"
            />
            <text x={CHART_PADDING.left - 8} y={t.y + 4} textAnchor="end" fontSize="10" fill="#666">
              {t.label}
            </text>
          </g>
        ))}
        {hasAmountAxis &&
          yTicksAmount.map((t, i) => (
            <text key={`a-${i}`} x={plotRight + 6} y={t.y + 4} textAnchor="start" fontSize="10" fill="#64748b">
              {t.label}
            </text>
          ))}
        {xTicks.map((t, i) => (
          <text key={i} x={t.x} y={CHART_HEIGHT - 8} textAnchor="middle" fontSize="10" fill="#666">
            {t.label}
          </text>
        ))}
        {pathsRate.map((path, i) => (
          <path
            key={`r-${i}`}
            d={path}
            fill="none"
            stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {hasAmountAxis &&
          pathsAmount.map((path, i) =>
            path.trim() ? (
              <path
                key={`m-${i}`}
                d={path}
                fill="none"
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                strokeWidth="1.5"
                strokeDasharray="5 4"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={0.92}
              />
            ) : null
          )}
      </svg>
      <div className="loan-info-legend">
        {series.map((s, i) => (
          <span key={s.key} className="loan-info-legend-item">
            <span className="loan-info-legend-dot" style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }} />
            {s.key}
          </span>
        ))}
        {hasAmountAxis && (
          <span className="loan-info-legend-note">实线：小时利率（左轴）· 虚线：最大可借（右轴）</span>
        )}
      </div>
    </div>
  );
}

function toLocalDatetimeValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localDatetimeToIso(local: string): string {
  const d = new Date(local);
  return d.toISOString();
}

export function LoanInfo() {
  const [exchange, setExchange] = useState<string>(LOAN_EXCHANGES[0].value);
  const [currency, setCurrency] = useState('BTC');
  const [vipLevel, setVipLevel] = useState('');
  const [lookbackHours, setLookbackHours] = useState(72);
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const [startLocal, setStartLocal] = useState(() => toLocalDatetimeValue(weekAgo));
  const [endLocal, setEndLocal] = useState(() => toLocalDatetimeValue(now));

  const [currentRows, setCurrentRows] = useState<LoanSnapshotRow[]>([]);
  const [historyRows, setHistoryRows] = useState<LoanSnapshotRow[]>([]);
  const [currentMeta, setCurrentMeta] = useState<{ lookback_hours: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const historySeries = useMemo(() => {
    const byKey = new Map<string, LoanChartPoint[]>();
    for (const r of historyRows) {
      if (r.hourly_borrow_rate == null || Number.isNaN(r.hourly_borrow_rate)) continue;
      const t = parseLoanTs(r.ts);
      if (!t) continue;
      const key = r.vip_level ? `VIP: ${r.vip_level}` : '默认档位';
      const maxAmount =
        r.max_borrow_amount != null && !Number.isNaN(r.max_borrow_amount) ? r.max_borrow_amount : null;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key)!.push({ t, rate: r.hourly_borrow_rate, maxAmount });
    }
    return Array.from(byKey.entries()).map(([key, points]) => ({ key, points }));
  }, [historyRows]);

  const fetchAll = useCallback(async () => {
    const ccy = currency.trim().toUpperCase();
    if (!ccy) {
      setError('请填写币种代码（如 BTC、USDT）');
      return;
    }
    setError(null);
    setLoading(true);
    const vip = vipLevel.trim();
    const optVip = vip || undefined;
    try {
      const startIso = localDatetimeToIso(startLocal);
      const endIso = localDatetimeToIso(endLocal);
      if (new Date(startIso) >= new Date(endIso)) {
        setError('历史结束时间必须晚于开始时间');
        setLoading(false);
        return;
      }

      const [cur, hist] = await Promise.all([
        getLoanRatesCurrent({
          exchange,
          currency: ccy,
          vip_level: optVip,
          lookback_hours: lookbackHours,
        }),
        getLoanRatesHistory({
          exchange,
          currency: ccy,
          start_time: startIso,
          end_time: endIso,
          vip_level: optVip,
          limit: 5000,
        }),
      ]);

      setCurrentMeta({ lookback_hours: cur.lookback_hours });
      setCurrentRows(cur.data.map((r) => normalizeRow(r as unknown as Record<string, unknown>)));
      setHistoryRows(hist.data.map((r) => normalizeRow(r as unknown as Record<string, unknown>)));
    } catch (e) {
      setCurrentRows([]);
      setHistoryRows([]);
      setCurrentMeta(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [currency, exchange, endLocal, lookbackHours, startLocal, vipLevel]);

  return (
    <div className="loan-info-page">
      <div className="loan-info-header">
        <h2>借币信息</h2>
        <p className="loan-info-desc">
          对接 python/loan-info：选择交易所与<strong>借贷币种</strong>（如 BTC、USDT，不是合约 instId）。Bybit 多 VIP 档位可同时存在多行，可用「VIP 档位」过滤。
        </p>
      </div>

      <div className="loan-info-controls">
        <label>
          <span>交易所</span>
          <select value={exchange} onChange={(e) => setExchange(e.target.value)}>
            {LOAN_EXCHANGES.map((ex) => (
              <option key={ex.value} value={ex.value}>
                {ex.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>币种</span>
          <input
            className="loan-info-currency"
            type="text"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            placeholder="BTC"
            autoCapitalize="characters"
          />
        </label>
        <label>
          <span>VIP 档位（可选）</span>
          <input type="text" value={vipLevel} onChange={(e) => setVipLevel(e.target.value)} placeholder="如 No VIP" />
        </label>
        <label>
          <span>当前快照回溯(小时)</span>
          <input
            type="number"
            min={1}
            max={336}
            value={lookbackHours}
            onChange={(e) => setLookbackHours(Number(e.target.value) || 48)}
            style={{ width: '80px' }}
          />
        </label>
        <label>
          <span>历史开始</span>
          <input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
        </label>
        <label>
          <span>历史结束</span>
          <input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} />
        </label>
        <button type="button" className="loan-info-btn" onClick={() => void fetchAll()} disabled={loading}>
          {loading ? '查询中…' : '查询'}
        </button>
      </div>
      <p className="loan-info-hint">
        API 基址：<code>{API_CONFIG.LOAN_INFO_PATH_PREFIX}</code>（<code>VITE_LOAN_INFO_PATH_PREFIX</code>）
      </p>

      {error && <div className="loan-info-error">{error}</div>}

      <section className="loan-info-section">
        <h3>当前借贷信息</h3>
        {currentMeta && (
          <p className="loan-info-meta">
            回溯窗口：<strong>{currentMeta.lookback_hours}</strong> 小时内各档位最新一条，共 <strong>{currentRows.length}</strong> 条
          </p>
        )}
        <div className="loan-info-table-wrap">
          <table className="loan-info-table">
            <thead>
              <tr>
                <th>时间 (UTC)</th>
                <th>VIP 档位</th>
                <th className="num">小时利率</th>
                <th className="num">最大可借</th>
                <th>可借</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ color: '#9ca3af' }}>
                    无数据，请查询或确认 loan-info 服务与 ClickHouse 已有采集数据
                  </td>
                </tr>
              ) : (
                currentRows.map((r, i) => (
                  <tr key={`${r.ts}-${r.vip_level}-${i}`}>
                    <td>{formatTs(r.ts)}</td>
                    <td>{r.vip_level || '—'}</td>
                    <td className="num">{formatRate(r.hourly_borrow_rate)}</td>
                    <td className="num">{formatAmount(r.max_borrow_amount)}</td>
                    <td>{r.borrowable == null ? '—' : r.borrowable ? '是' : '否'}</td>
                    <td>{r.note ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="loan-info-section">
        <h3>历史借贷信息</h3>
        <p className="loan-info-meta">
          共 <strong>{historyRows.length}</strong> 条；下图按 VIP 档位展示<strong>小时借款利率</strong>（左轴）；若历史中存在{' '}
          <strong>最大可借</strong> 字段则叠加<strong>虚线</strong>（右轴，与利率刻度独立）
        </p>
        <div className="loan-info-chart-section">
          <LoanRateChart series={historySeries} />
        </div>
        <div className="loan-info-table-wrap" style={{ marginTop: '1rem' }}>
          <table className="loan-info-table">
            <thead>
              <tr>
                <th>时间 (UTC)</th>
                <th>VIP 档位</th>
                <th className="num">小时利率</th>
                <th className="num">最大可借</th>
                <th>可借</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ color: '#9ca3af' }}>
                    无历史数据
                  </td>
                </tr>
              ) : (
                [...historyRows]
                  .sort((a, b) => parseLoanTs(b.ts) - parseLoanTs(a.ts))
                  .map((r, i) => (
                    <tr key={`h-${r.ts}-${r.vip_level}-${i}`}>
                      <td>{formatTs(r.ts)}</td>
                      <td>{r.vip_level || '—'}</td>
                      <td className="num">{formatRate(r.hourly_borrow_rate)}</td>
                      <td className="num">{formatAmount(r.max_borrow_amount)}</td>
                      <td>{r.borrowable == null ? '—' : r.borrowable ? '是' : '否'}</td>
                      <td>{r.note ?? '—'}</td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
